import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RenameTagRequest, } from '@wit-support/admin-contracts';
import { Dialog, DialogContent, DialogDescription, DialogTitle, toast } from '@wit-support/ui';
import { Actions, SubmitButton, ZodField, ZodForm, CancelButton } from '@wit-support/ui-forms';
import { ModuleRequestError } from '@wit-support/admin-client-core';
import { renameTag } from '../api.js';
/**
 * Русские тексты по коду сервера для `PATCH /api/tags/:id` — кроме 409, который
 * уходит не в тост, а в ошибку ПОЛЯ (см. `onServerError`). Незнакомый код
 * получает нейтральную фразу, а не сырой `not_found`.
 */
const RENAME_TAG_ERROR_RU = {
    not_found: 'Тег не найден',
    forbidden: 'Недостаточно прав',
    invalid_body: 'Недопустимое имя тега',
    invalid_name: 'Недопустимое имя тега',
};
const RENAME_TAG_ERROR_DEFAULT = 'Не удалось переименовать тег';
export function renameTagErrorMessage(err) {
    const code = err instanceof ModuleRequestError ? err.code : null;
    return (code != null ? RENAME_TAG_ERROR_RU[code] : undefined) ?? RENAME_TAG_ERROR_DEFAULT;
}
/**
 * Переименование тега.
 *
 * Тег общий, поэтому переименование видно всем и во всех диалогах сразу —
 * об этом говорит подпись, а не только результат.
 */
export function RenameTagDialog({ tag, onClose }) {
    const qc = useQueryClient();
    const mut = useMutation({
        mutationFn: (body) => renameTag(tag.id, body),
        onSuccess: (updated) => {
            toast.success(`Тег переименован в «${updated.name}»`);
            void qc.invalidateQueries({ queryKey: ['tags'] });
            // Имя тега нарисовано в строках инбокса и в рейле диалога — без сброса
            // там останется старое, пока оператор не перезагрузит страницу.
            void qc.invalidateQueries({ queryKey: ['conversations'] });
            onClose();
        },
    });
    return (_jsx(Dialog, { open: true, onOpenChange: (v) => (!v ? onClose() : null), children: _jsxs(DialogContent, { children: [_jsx(DialogTitle, { children: "\u041F\u0435\u0440\u0435\u0438\u043C\u0435\u043D\u043E\u0432\u0430\u0442\u044C \u0442\u0435\u0433" }), _jsx(DialogDescription, { children: "\u0422\u0435\u0433 \u043E\u0431\u0449\u0438\u0439: \u043D\u043E\u0432\u043E\u0435 \u0438\u043C\u044F \u0443\u0432\u0438\u0434\u044F\u0442 \u0432\u0441\u0435 \u043E\u043F\u0435\u0440\u0430\u0442\u043E\u0440\u044B \u0438 \u0432\u0441\u0435 \u0434\u0438\u0430\u043B\u043E\u0433\u0438, \u0433\u0434\u0435 \u043E\u043D \u0441\u0442\u043E\u0438\u0442." }), _jsxs(ZodForm, { schema: RenameTagRequest, defaultValues: { name: tag.name }, onSubmit: async (values) => {
                        await mut.mutateAsync(values);
                    }, 
                    /*
                      409 — не сбой запроса, а занятое имя, и место такому сообщению у
                      ПОЛЯ: тост оператор закроет, а поле останется подсвеченным, и
                      исправлять он будет ровно то, что ввёл. Разбирается по СТАТУСУ, а
                      не по подстроке в тексте: текст меняется, статус — нет.
                    */
                    onServerError: (err, form) => {
                        if (err instanceof ModuleRequestError && err.status === 409) {
                            form.setError('name', { type: 'server', message: 'Такой тег уже есть' });
                            return;
                        }
                        toast.error(renameTagErrorMessage(err));
                    }, children: [_jsx(ZodField, { name: "name", label: "\u0418\u043C\u044F \u0442\u0435\u0433\u0430" }), _jsxs(Actions, { children: [_jsx(CancelButton, { onClick: onClose }), _jsx(SubmitButton, { pendingLabel: "\u0421\u043E\u0445\u0440\u0430\u043D\u0435\u043D\u0438\u0435\u2026", children: "\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C" })] })] })] }) }));
}
//# sourceMappingURL=rename-tag-dialog.js.map